import type { Deviation, DeviationType } from "../types/entities.js";

// Major/Critical deviations must be reported to the sponsor promptly (FDA/EMA
// guidance: commonly within 10 working days of awareness) - Minor and
// Planned deviations don't carry the same mandatory-notification weight.
export const SPONSOR_REPORT_REQUIRED_TYPES: DeviationType[] = ["CRITICAL", "MAJOR"];
export const SPONSOR_REPORT_WORKING_DAYS = 10;

// Adds `days` working days (Mon-Fri) to an ISO date string, returning an ISO
// date string. Pure date-only arithmetic - no timezone/time-of-day handling
// needed since deviation dates are stored as plain date strings.
export function addWorkingDays(fromISODate: string, days: number): string {
  const date = new Date(`${fromISODate}T00:00:00Z`);
  let remaining = days;
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const day = date.getUTCDay();
    if (day !== 0 && day !== 6) remaining--;
  }
  return date.toISOString().slice(0, 10);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// null if this deviation's type carries no mandatory sponsor-notification
// deadline, or if there's no occurrence date to count from yet.
// occurrenceDate/date are free-text columns (not a DB date type - see
// deviationRoutes.ts), so real data includes non-dates: "—" placeholders,
// date ranges ("2026-03-28 → 2026-04-28"), empty strings. Anything that
// isn't a clean YYYY-MM-DD is treated the same as "no date yet" rather than
// passed to Date parsing, which would silently produce an Invalid Date and
// crash addWorkingDays's toISOString() call.
export function sponsorReportDeadline(dev: Pick<Deviation, "dvcat" | "dvstdat" | "date">): string | null {
  if (!SPONSOR_REPORT_REQUIRED_TYPES.includes(dev.dvcat)) return null;
  const from = dev.dvstdat ?? dev.date;
  if (!from || !ISO_DATE_RE.test(from)) return null;
  return addWorkingDays(from, SPONSOR_REPORT_WORKING_DAYS);
}

// Computed at read time, never persisted (same convention as clinical-data's
// derived vitals/tumor-volume fields). Compares the deadline against the
// actual report date if already reported, otherwise against `asOf` (now).
export function isSponsorReportOverdue(
  dev: Pick<Deviation, "dvcat" | "dvstdat" | "date" | "reportedToSponsor" | "sponsorReportDate">,
  asOf: Date = new Date()
): boolean {
  const deadline = sponsorReportDeadline(dev);
  if (!deadline) return false;
  const compareDate = dev.reportedToSponsor && dev.sponsorReportDate ? dev.sponsorReportDate : asOf.toISOString().slice(0, 10);
  return compareDate > deadline;
}
